import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import axios from 'axios';
import { Modal, Button, Form, Alert, InputGroup } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';

const UserList = () => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    
    // Form states
    const [currentUser, setCurrentUser] = useState({
        nama_lengkap: '',
        jenis_kelamin: '',
        nomor_wa: '',
        password: '',
        type: '',
    });

    // Fetch users
    const fetchUsers = async () => {
        try {
            const response = await axios.get('https://api.tomassage.id/api/users');
            setUsers(response.data.data);
            setFilteredUsers(response.data.data);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Gagal memuat daftar pengguna');
            setLoading(false);
        }
    };

    // Search users
    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);
        
        if (!value.trim()) {
            setFilteredUsers(users);
            return;
        }
        
        const filtered = users.filter(
            user => 
                user.nama_lengkap?.toLowerCase().includes(value) || 
                user.nomor_wa?.toLowerCase().includes(value) ||
                (user.type?.toLowerCase().includes(value)) ||
                user.id?.toString().includes(value)
        );
        
        setFilteredUsers(filtered);
    };

    // Reset search
    const resetSearch = () => {
        setSearchTerm('');
        setFilteredUsers(users);
    };

    // Initial fetch
    useEffect(() => {
        fetchUsers();
    }, []);

    // Reset form and messages
    const resetForm = () => {
        setCurrentUser({
            nama_lengkap: '',
            jenis_kelamin: '',
            nomor_wa: '',
            password: '',
            type: '',
        });
        setError(null);
        setSuccessMessage(null);
    };

    // Add User
    const handleAddUser = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post('https://api.tomassage.id/api/users/register', currentUser);
            
            if (response.data.success) {
                fetchUsers();
                setShowAddModal(false);
                setSuccessMessage('Pengguna berhasil ditambahkan');
                resetForm();
            } else {
                setError(response.data.message || 'Gagal menambahkan pengguna');
            }
        } catch (err) {
            console.error('Error adding user:', err);
            setError(err.response?.data?.message || 'Gagal menambahkan pengguna');
        }
    };

    // Edit User
    const handleEditUser = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.put(`https://api.tomassage.id/api/users/${currentUser.id}`, currentUser);
            
            if (response.data.success) {
                fetchUsers();
                setShowEditModal(false);
                setSuccessMessage('Pengguna berhasil diperbarui');
                resetForm();
            } else {
                setError(response.data.message || 'Gagal mengubah pengguna');
            }
        } catch (err) {
            console.error('Error editing user:', err);
            setError(err.response?.data?.message || 'Gagal mengubah pengguna');
        }
    };

    // Delete User
    const handleDeleteUser = async () => {
        try {
            const response = await axios.delete(`https://api.tomassage.id/api/users/${currentUser.id}`);
            
            if (response.data.success) {
                fetchUsers();
                setShowDeleteModal(false);
                setSuccessMessage('Pengguna berhasil dihapus');
                resetForm();
            } else {
                setError(response.data.message || 'Gagal menghapus pengguna');
            }
        } catch (err) {
            console.error('Error deleting user:', err);
            setError(err.response?.data?.message || 'Gagal menghapus pengguna');
        }
    };

    // Render loading
    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid p-4">
            {/* Success Message */}
            {successMessage && (
                <Alert 
                    variant="success" 
                    onClose={() => setSuccessMessage(null)} 
                    dismissible
                >
                    {successMessage}
                </Alert>
            )}

            {/* Error Message */}
            {error && (
                <Alert 
                    variant="danger" 
                    onClose={() => setError(null)} 
                    dismissible
                >
                    {error}
                </Alert>
            )}

            <div className="card shadow-sm">
                <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">
                        <Icon icon="mdi:users" className="me-2" />
                        Daftar Pengguna
                    </h4>
                    <Button 
                        variant="light" 
                        onClick={() => {
                            resetForm();
                            setShowAddModal(true);
                        }}
                    >
                        <Icon icon="mdi:plus" className="me-1" />
                        Tambah Pengguna
                    </Button>
                </div>
                <div className="card-body">
                    {/* Search Box */}
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <InputGroup>
                                <Form.Control
                                    placeholder="Cari pengguna berdasarkan nama, nomor WA, tipe atau ID..."
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                                {searchTerm && (
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={resetSearch}
                                    >
                                        <Icon icon="mdi:close" />
                                    </Button>
                                )}
                                <Button variant="outline-primary">
                                    <Icon icon="mdi:magnify" />
                                </Button>
                            </InputGroup>
                        </div>
                        <div className="col-md-6 text-end">
                            {filteredUsers.length < users.length && (
                                <span className="text-muted">
                                    Menampilkan {filteredUsers.length} dari {users.length} pengguna
                                </span>
                            )}
                        </div>
                    </div>
                    
                    <div className="table-responsive">
                        <table className="table table-striped table-hover mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>ID</th>
                                    <th>Nama Lengkap</th>
                                    <th>Jenis Kelamin</th>
                                    <th>Nomor WA</th>
                                    <th>Tipe</th>
                                    <th>Tanggal Dibuat</th>
                                    <th className="text-center">Aksi</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.length > 0 ? (
                                    filteredUsers.map((user) => (
                                        <tr key={user.id}>
                                            <td>{user.id}</td>
                                            <td>{user.nama_lengkap}</td>
                                            <td>{user.jenis_kelamin}</td>
                                            <td>{user.nomor_wa}</td>
                                            <td>
                                                <span 
                                                    className={`badge ${user.type ? 'bg-success' : 'bg-warning'}`}
                                                >
                                                    {user.type || 'Tidak Diset'}
                                                </span>
                                            </td>
                                            <td>
                                                {new Date(user.created_at).toLocaleDateString('id-ID', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                            </td>
                                            <td className="text-center">
                                                <div className="btn-group" role="group">
                                                    <Button 
                                                        variant="info" 
                                                        size="sm" 
                                                        className="me-1"
                                                        onClick={() => {
                                                            setCurrentUser(user);
                                                            setShowDetailModal(true);
                                                        }}
                                                    >
                                                        <Icon icon="mdi:eye" />
                                                    </Button>
                                                    <Button 
                                                        variant="warning" 
                                                        size="sm" 
                                                        className="me-1"
                                                        onClick={() => {
                                                            setCurrentUser({...user, password: ''}); // Reset password
                                                            setShowEditModal(true);
                                                        }}
                                                    >
                                                        <Icon icon="mdi:pencil" />
                                                    </Button>
                                                    <Button 
                                                        variant="danger" 
                                                        size="sm"
                                                        onClick={() => {
                                                            setCurrentUser(user);
                                                            setShowDeleteModal(true);
                                                        }}
                                                    >
                                                        <Icon icon="mdi:delete" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center">
                                            {searchTerm ? (
                                                <>
                                                    <Icon icon="mdi:file-search-outline" className="me-2" size="24" />
                                                    Tidak ada pengguna yang cocok dengan pencarian
                                                </>
                                            ) : (
                                                'Tidak ada data pengguna'
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Add User Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Tambah Pengguna Baru</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleAddUser}>
                        <Form.Group className="mb-3">
                            <Form.Label>Nama Lengkap</Form.Label>
                            <Form.Control 
                                type="text" 
                                value={currentUser.nama_lengkap}
                                onChange={(e) => setCurrentUser({...currentUser, nama_lengkap: e.target.value})}
                                required 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Jenis Kelamin</Form.Label>
                            <Form.Select 
                                value={currentUser.jenis_kelamin}
                                onChange={(e) => setCurrentUser({...currentUser, jenis_kelamin: e.target.value})}
                                required
                            >
                                <option value="">Pilih Jenis Kelamin</option>
                                <option value="Laki-laki">Laki-laki</option>
                                <option value="Perempuan">Perempuan</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Nomor WA</Form.Label>
                            <Form.Control 
                                type="text" 
                                value={currentUser.nomor_wa}
                                onChange={(e) => setCurrentUser({...currentUser, nomor_wa: e.target.value})}
                                required 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Password</Form.Label>
                            <Form.Control 
                                type="password" 
                                value={currentUser.password}
                                onChange={(e) => setCurrentUser({...currentUser, password: e.target.value})}
                                required 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Tipe</Form.Label>
                            <Form.Select 
                                value={currentUser.type || ''}
                                onChange={(e) => setCurrentUser({...currentUser, type: e.target.value})}
                            >
                                <option value="">Pilih Tipe</option>
                                <option value="user">User</option>
                                <option value="mitra">Mitra</option>
                            </Form.Select>
                        </Form.Group>
                        <Button variant="primary" type="submit">
                            Tambah Pengguna
                        </Button>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Edit User Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Pengguna</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleEditUser}>
                        <Form.Group className="mb-3">
                            <Form.Label>Nama Lengkap</Form.Label>
                            <Form.Control 
                                type="text" 
                                value={currentUser.nama_lengkap}
                                onChange={(e) => setCurrentUser({...currentUser, nama_lengkap: e.target.value})}
                                required 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Jenis Kelamin</Form.Label>
                            <Form.Select 
                                value={currentUser.jenis_kelamin}
                                onChange={(e) => setCurrentUser({...currentUser, jenis_kelamin: e.target.value})}
                                required
                            >
                                <option value="">Pilih Jenis Kelamin</option>
                                <option value="Laki-laki">Laki-laki</option>
                                <option value="Perempuan">Perempuan</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Nomor WA</Form.Label>
                            <Form.Control 
                                type="text" 
                                value={currentUser.nomor_wa}
                                onChange={(e) => setCurrentUser({...currentUser, nomor_wa: e.target.value})}
                                required 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Password (Kosongkan jika tidak ingin mengubah)</Form.Label>
                            <Form.Control 
                                type="password" 
                                value={currentUser.password || ''}
                                onChange={(e) => setCurrentUser({...currentUser, password: e.target.value})}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Tipe</Form.Label>
                            <Form.Select 
                                value={currentUser.type || ''}
                                onChange={(e) => setCurrentUser({...currentUser, type: e.target.value})}
                            >
                                <option value="">Pilih Tipe</option>
                                <option value="user">User</option>
                                <option value="mitra">Mitra</option>
                            </Form.Select>
                        </Form.Group>
                        <div className="d-flex justify-content-between">
                            <Button variant="primary" type="submit">
                                <Icon icon="mdi:content-save" className="me-1" />
                                Simpan Perubahan
                            </Button>
                            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                                Batal
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Detail User Modal */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Detail Pengguna</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <table className="table">
                        <tbody>
                            <tr>
                                <th>ID</th>
                                <td>{currentUser.id}</td>
                            </tr>
                            <tr>
                                <th>Nama Lengkap</th>
                                <td>{currentUser.nama_lengkap}</td>
                            </tr>
                            <tr>
                                <th>Jenis Kelamin</th>
                                <td>{currentUser.jenis_kelamin}</td>
                            </tr>
                            <tr>
                                <th>Nomor WA</th>
                                <td>{currentUser.nomor_wa}</td>
                            </tr>
                            <tr>
                                <th>Tipe</th>
                                <td><span 
                                        className={`badge ${currentUser.type ? 'bg-success' : 'bg-warning'}`}
                                    >
                                        {currentUser.type || 'Tidak Diset'}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <th>Tanggal Dibuat</th>
                                <td>
                                    {currentUser.created_at 
                                        ? new Date(currentUser.created_at).toLocaleString('id-ID', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        }) 
                                        : '-'
                                    }
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="warning" 
                        onClick={() => {
                            setCurrentUser({...currentUser, password: ''});
                            setShowDetailModal(false);
                            setShowEditModal(true);
                        }}
                    >
                        <Icon icon="mdi:pencil" className="me-1" />
                        Edit Pengguna
                    </Button>
                    <Button 
                        variant="secondary" 
                        onClick={() => setShowDetailModal(false)}
                    >
                        Tutup
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Konfirmasi Hapus</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Apakah Anda yakin ingin menghapus pengguna <strong>{currentUser.nama_lengkap}</strong>?</p>
                    <p className="text-danger">
                        <Icon icon="mdi:alert" className="me-1" />
                        Tindakan ini tidak dapat dibatalkan!
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Batal
                    </Button>
                    <Button variant="danger" onClick={handleDeleteUser}>
                        <Icon icon="mdi:delete" className="me-1" />
                        Hapus
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default UserList;